import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, Users, LogIn, Rocket, Activity, AlertTriangle, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import Breadcrumbs from "@/components/admin/Breadcrumbs";

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`platform-kpi-${label.toLowerCase().replace(/\s+/g,"-")}`}>
      <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-slate-500">
        <Icon className="h-3.5 w-3.5" />{label}
      </div>
      <div className="mt-2 font-display text-3xl font-semibold tracking-tight text-slate-900">{value ?? "—"}</div>
    </div>
  );
}

function TenantPill({ tenant }) {
  if (!tenant) return <span className="text-[10px] text-slate-400">—</span>;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white" style={{ background: tenant.primary_color }}>
      {tenant.name}
    </span>
  );
}

export default function PlatformHome() {
  const [kpis, setKpis] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [activity, setActivity] = useState([]);
  const [suspicious, setSuspicious] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [k, t, a, s] = await Promise.all([
          api.get("/platform/rollup/kpis"),
          api.get("/platform/rollup/tenants"),
          api.get("/platform/rollup/recent-activity?limit=20"),
          api.get("/platform/rollup/suspicious"),
        ]);
        if (!alive) return;
        setKpis(k.data); setTenants(t.data); setActivity(a.data); setSuspicious(s.data);
      } catch (err) {
        // Polling rollup — degrade to stale data, but surface failures to devtools.
        console.error("platform rollup fetch failed", err);
      }
    };
    load();
    const iv = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  return (
    <div data-testid="platform-home" className="mx-auto w-full max-w-7xl space-y-8">
      <Breadcrumbs />
      <div>
        <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">Platform rollup</h2>
        <p className="mt-1 text-sm text-slate-500">Cross-tenant snapshot across BlackScott Holdings.</p>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Building2} label="Active tenants" value={kpis?.active_tenants} />
        <Kpi icon={Users} label="Active users" value={kpis?.active_users} />
        <Kpi icon={LogIn} label="Logins 24h" value={kpis?.logins_24h} />
        <Kpi icon={Rocket} label="SSO today" value={kpis?.sso_launches_today} />
      </div>

      <section>
        <h3 className="mb-3 font-display text-lg font-semibold text-slate-900">Per-tenant</h3>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Tenant</th>
                <th className="px-4 py-2 text-right">Members</th>
                <th className="px-4 py-2 text-right">Apps</th>
                <th className="px-4 py-2 text-right">Logins 24h</th>
                <th className="px-4 py-2 text-right">Audit 24h</th>
                <th className="px-4 py-2 text-left">Last activity</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`platform-tenant-row-${t.slug}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.primary_color }} />
                      <span className="font-medium text-slate-800">{t.name}</span>
                      {t.is_platform && <span className="rounded bg-slate-900 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-300">Platform</span>}
                      {t.status !== "active" && <span className="rounded bg-slate-200 px-1 py-0.5 text-[9px] font-bold uppercase text-slate-600">{t.status}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">{t.members}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">{t.grants}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">{t.logins_24h}</td>
                  <td className="px-4 py-2 text-right font-mono text-xs text-slate-700">{t.audit_24h}</td>
                  <td className="px-4 py-2 text-xs text-slate-500">{t.last_activity ? new Date(t.last_activity).toLocaleString() : "—"}</td>
                  <td className="px-4 py-2">
                    <Link to={`/platform/tenants/${t.slug}`} data-testid={`platform-tenant-open-${t.slug}`} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-800">Open<ArrowRight className="h-3 w-3"/></Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section>
          <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-slate-900"><Activity className="h-4 w-4" />Recent activity</h3>
          <div className="max-h-[420px] overflow-y-auto rounded-xl border border-slate-200 bg-white">
            {(activity || []).map(r => (
              <div key={r.id} className="flex items-start gap-3 border-b border-slate-100 px-3 py-2 text-xs last:border-b-0">
                <span className="text-[10px] text-slate-400 w-24 shrink-0">{new Date(r.ts).toLocaleTimeString()}</span>
                <span className="flex-1">
                  <span className="font-mono text-[11px] text-slate-700">{r.action}</span>
                  {r.target_label && <span className="text-slate-500"> · {r.target_label}</span>}
                  {r.actor_email && <span className="text-slate-400"> · {r.actor_email}</span>}
                </span>
                <TenantPill tenant={r.tenant} />
              </div>
            ))}
            {(!activity || activity.length === 0) && <div className="p-6 text-center text-xs text-slate-400">No activity yet.</div>}
          </div>
        </section>
        <section>
          <h3 className="mb-3 flex items-center gap-2 font-display text-lg font-semibold text-slate-900"><AlertTriangle className="h-4 w-4" />Suspicious activity</h3>
          <div className="rounded-xl border border-slate-200 bg-white p-5 text-sm">
            {(!suspicious || (suspicious.high_failure_groups.length === 0 && suspicious.locked_accounts === 0)) ? (
              <div className="flex items-center gap-2 text-slate-500"><span className="text-emerald-500">●</span>All clear across the group.</div>
            ) : (
              <ul className="space-y-2">
                {(suspicious?.high_failure_groups || []).map((g) => (
                  <li key={g.email} className="flex items-center justify-between rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs">
                    <span><span className="font-mono">{g.email}</span> · {g.count} failures</span>
                  </li>
                ))}
                {suspicious?.locked_accounts > 0 && (
                  <li className="rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs">
                    {suspicious.locked_accounts} account{suspicious.locked_accounts === 1 ? "" : "s"} currently in lockout.
                  </li>
                )}
              </ul>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
